import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Linkedin, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const EMAIL = "nishantgathwal@gmail.com";
const LINKEDIN = "https://www.linkedin.com/in/nishant-gathwal-8494b047/";

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-2">
    <label htmlFor={htmlFor} className="block text-xs tracking-[0.18em] uppercase text-ash">
      {label}
    </label>
    {children}
  </div>
);

const inputClass =
  "w-full bg-transparent border-b border-white/20 focus:border-cream text-cream placeholder:text-ash/60 py-3 outline-none transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload) => api.post("/contact", payload).then((r) => r.data),
    onSuccess: () => {
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — I'll get back to you soon.");
    },
    onError: () => toast.error("Something went wrong. Please try again or email me directly."),
  });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    mutation.mutate(form);
  };

  return (
    <div data-testid="contact-page" className="pt-36 lg:pt-44 pb-24 lg:pb-32">
      <Seo
        title="Contact"
        description="Get in touch with Nishant Gathwal — UX designer in New Delhi available for product design, design systems, and UX consulting."
        path="/contact"
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Left intro */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs tracking-[0.2em] uppercase text-ash mb-5">Contact</p>
              <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream text-balance">
                Let's start a conversation.
              </h1>
              <p className="mt-6 text-ash text-lg leading-relaxed max-w-md">
                Whether it's a product to design, a problem to untangle, or just a hello — I'd love to hear from you.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-12 space-y-5">
                <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="group flex items-center gap-4 text-cream hover:text-white transition-colors">
                  <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <Mail size={18} />
                  </span>
                  {EMAIL}
                </a>
                <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" className="group flex items-center gap-4 text-cream hover:text-white transition-colors">
                  <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                    <Linkedin size={18} />
                  </span>
                  LinkedIn
                </a>
                <div className="flex items-center gap-4 text-ash">
                  <span className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center">
                    <MapPin size={18} />
                  </span>
                  New Delhi, India
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div data-testid="contact-success" className="rounded-2xl border border-white/10 bg-surface p-10 lg:p-14 text-center">
                  <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-6" strokeWidth={1.5} />
                  <h2 className="font-heading font-light text-3xl tracking-tight text-cream">Message sent</h2>
                  <p className="mt-4 text-ash">Thanks for reaching out — I'll reply to your email shortly.</p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-send-another"
                    className="mt-8 inline-flex rounded-full border border-white/20 text-cream px-6 py-3 hover:bg-white/10 transition-colors"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-2xl border border-white/10 bg-surface p-8 lg:p-12 space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <Field label="Your name" htmlFor="name">
                      <input id="name" name="name" data-testid="contact-input-name" value={form.name} onChange={onChange} placeholder="Jane Doe" className={inputClass} />
                    </Field>
                    <Field label="Email" htmlFor="email">
                      <input id="email" name="email" type="email" data-testid="contact-input-email" value={form.email} onChange={onChange} placeholder="jane@company.com" className={inputClass} />
                    </Field>
                  </div>
                  <Field label="Subject" htmlFor="subject">
                    <input id="subject" name="subject" data-testid="contact-input-subject" value={form.subject} onChange={onChange} placeholder="What's this about?" className={inputClass} />
                  </Field>
                  <Field label="Message" htmlFor="message">
                    <textarea id="message" name="message" data-testid="contact-input-message" value={form.message} onChange={onChange} rows={5} placeholder="Tell me a little about your project…" className={`${inputClass} resize-none`} />
                  </Field>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={mutation.isPending}
                    className="group inline-flex items-center gap-2 rounded-full bg-cream text-ink font-medium px-8 py-4 hover:bg-white transition-colors disabled:opacity-60"
                  >
                    {mutation.isPending ? (
                      <><Loader2 size={18} className="animate-spin" /> Sending…</>
                    ) : (
                      <>Send message <Send size={16} className="transition-transform group-hover:translate-x-0.5" /></>
                    )}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
